import type { InputPrimaryEvent, NativeSelectOption } from '@shared/ui/fields'
import type { LessonRecurringDayForm } from './lesson-recurring'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { useToast } from '@shared/ui/toast'

import { LessonRecurringFinishType, LessonRecurringModal } from './enums'
import { useLessonRecurringStore } from './recurring/store'
import { useDisciplineStore } from '../store'
import { useLessonRecurringSubmit } from './lesson-recurring-submit.hook'
import { useLessonRecurringTutors } from './lesson-recurring-tutors.hook'
import {
  LESSON_RECURRING_DAYS,
  LESSON_RECURRING_WEEK_CYCLES,
  getCheckedRecurringDays,
  getWeekdayOfDate,
  isRecurringScheduleOnly,
} from './lesson-recurring'

interface LessonRecurringDayTab {
  day: string
  selected: boolean
  checked: boolean
}

interface LessonRecurringScheduleReturn {
  refStartDate: ComputedRef<string>
  refMinDate: ComputedRef<string>
  refDayTabs: ComputedRef<LessonRecurringDayTab[]>
  refSelectedDayName: ComputedRef<string>
  refStartOptions: ComputedRef<NativeSelectOption[]>
  refDurationOptions: ComputedRef<NativeSelectOption[]>
  refWeekCycleOptions: ComputedRef<NativeSelectOption[]>
  refSelectedDay: ComputedRef<LessonRecurringDayForm | null>
  refBalance: ComputedRef<number | null>
  refFinishType: ComputedRef<LessonRecurringFinishType>
  refOccurrences: ComputedRef<number>
  refLessons: ComputedRef<number>
  refFinishDate: ComputedRef<string>
  refSubmitText: ComputedRef<string>
  refIsSubmitting: ComputedRef<boolean>
  refLessonsUrl: string
  setStartDate: (value: string) => void
  selectDay: (day: string) => void
  setStartTime: (option: NativeSelectOption) => void
  setDuration: (option: NativeSelectOption) => void
  setWeekCycle: (option: NativeSelectOption) => void
  setFinishType: (type: LessonRecurringFinishType) => void
  setOccurrences: (event: InputPrimaryEvent) => void
  setLessons: (event: InputPrimaryEvent) => void
  setFinishDate: (value: string) => void
  submit: () => Promise<void>
}

const MAX_COUNT = 200

const toCount = (value: string | number): number => {
  const count = Math.trunc(Number(value))

  return Number.isFinite(count) ? Math.min(Math.max(count, 0), MAX_COUNT) : 0
}

/** The schedule: on which days, at what time, from when and until when. Its confirmation from the card header opens
 *  the tutor modal, or for a schedule-only form puts the pattern on the discipline's tutor at once. */
export const useLessonRecurringSchedule = (): LessonRecurringScheduleReturn => {
  const route = useRoute()
  const toast = useToast()
  const store = useLessonRecurringStore()
  const disciplineStore = useDisciplineStore()
  const { submitRecurring } = useLessonRecurringSubmit()
  const { loadTutors } = useLessonRecurringTutors()

  const refLessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: route.params.id as string }) ?? ''

  const refStartDate = computed<string>(() => store.getStartDate)

  const refMinDate = computed<string>(() => store.getToday)

  const refDayTabs = computed<LessonRecurringDayTab[]>(() => store.getDays.map(item => ({
    day: item.day,
    selected: item.day === store.getSelectedDay,
    checked: item.startTime !== null,
  })))

  const refSelectedDay = computed<LessonRecurringDayForm | null>(() =>
    store.getDays.find(item => item.day === store.getSelectedDay) ?? null)

  const refSelectedDayName = computed<string>(() =>
    LESSON_RECURRING_DAYS.find(item => item.day === store.getSelectedDay)?.name ?? store.getSelectedDay)

  const refStartOptions = computed<NativeSelectOption[]>(() => {
    const selected = refSelectedDay.value?.startTime ?? null

    return [
      { id: 'none', value: null, text: 'Not selected', selected: selected === null },
      ...(store.getSlots?.start ?? []).map(slot => ({ id: slot, value: slot, text: slot, selected: slot === selected })),
    ]
  })

  const refDurationOptions = computed<NativeSelectOption[]>(() =>
    (store.getSlots?.duration ?? []).map(minutes => ({
      id: minutes,
      value: minutes,
      text: `${minutes} min`,
      selected: minutes === refSelectedDay.value?.duration,
    })))

  const refWeekCycleOptions = computed<NativeSelectOption[]>(() =>
    LESSON_RECURRING_WEEK_CYCLES.map(cycle => ({
      id: cycle,
      value: cycle,
      text: String(cycle),
      selected: cycle === refSelectedDay.value?.weekCycle,
    })))

  const refBalance = computed<number | null>(() => disciplineStore.getDetails?.unscheduled_bookings ?? null)

  const refFinishType = computed<LessonRecurringFinishType>(() => store.getFinishType)
  const refOccurrences = computed<number>(() => store.getOccurrences)
  const refLessons = computed<number>(() => store.getLessons)
  const refFinishDate = computed<string>(() => store.getFinishDate)

  const refIsScheduleOnly = computed<boolean>(() =>
    isRecurringScheduleOnly(store.getFinishType, store.getOccurrences, store.getLessons))

  const refSubmitText = computed<string>(() => (refIsScheduleOnly.value ? 'Confirm schedule' : 'Confirm to assign tutor'))

  const refIsSubmitting = computed<boolean>(() => store.isSubmitting)

  /** A new start date opens its weekday, and the end date may not fall before it. */
  const setStartDate = (value: string): void => {
    const date = value || store.getToday

    store.setStartDate(date)
    store.setSelectedDay(getWeekdayOfDate(date))
    if (store.getFinishDate < date) store.setFinishDate(date)
  }

  const selectDay = (day: string): void => {
    store.setSelectedDay(day)
  }

  const setStartTime = (option: NativeSelectOption): void => {
    store.patchDay(store.getSelectedDay, { startTime: option.value ? String(option.value) : null })
  }

  const setDuration = (option: NativeSelectOption): void => {
    store.patchDay(store.getSelectedDay, { duration: Number(option.value) })
  }

  const setWeekCycle = (option: NativeSelectOption): void => {
    store.patchDay(store.getSelectedDay, { weekCycle: Number(option.value) })
  }

  const setFinishType = (type: LessonRecurringFinishType): void => {
    store.setFinishType(type)
  }

  const setOccurrences = (event: InputPrimaryEvent): void => {
    store.setOccurrences(toCount(event.value))
  }

  const setLessons = (event: InputPrimaryEvent): void => {
    store.setLessons(toCount(event.value))
  }

  const setFinishDate = (value: string): void => {
    store.setFinishDate(value || store.getStartDate)
  }

  const submit = async (): Promise<void> => {
    const balance = refBalance.value

    if (!refIsScheduleOnly.value && balance !== null && balance <= 0) {
      toast.warning({
        title: `Lessons available for booking is: ${balance}`,
        description: 'Check the Balances page table!',
      })
      return
    }

    if (!getCheckedRecurringDays(store.getDays).length) {
      toast.warning({
        title: 'Empty list!',
        description: 'The repeat field does not contain any day from the list!',
      })
      return
    }

    if (!refIsScheduleOnly.value) {
      store.setModal(LessonRecurringModal.Tutor)
      await loadTutors()
      return
    }

    const tutorId = disciplineStore.getDiscipline?.tutor_id

    if (!tutorId) {
      toast.warning({
        title: 'Warning!',
        description: 'The discipline has no tutor to put on this schedule.',
      })
      return
    }

    await submitRecurring(tutorId)
  }

  return {
    refStartDate,
    refMinDate,
    refDayTabs,
    refSelectedDayName,
    refStartOptions,
    refDurationOptions,
    refWeekCycleOptions,
    refSelectedDay,
    refBalance,
    refFinishType,
    refOccurrences,
    refLessons,
    refFinishDate,
    refSubmitText,
    refIsSubmitting,
    refLessonsUrl,
    setStartDate,
    selectDay,
    setStartTime,
    setDuration,
    setWeekCycle,
    setFinishType,
    setOccurrences,
    setLessons,
    setFinishDate,
    submit,
  }
}
